import { useRef, useState } from "react";
import { Bold, Eye, Italic, Link2, List, ListOrdered, Pencil, Quote, Underline } from "lucide-react";
import { Markdown } from "./Markdown";

const wrap = (value, start, end, selection) => `${start}${selection || "texto"}${end}`;

const ACTIONS = [
  { id: "bold", label: "Negrito", icon: Bold, apply: (s) => wrap(null, "**", "**", s) },
  { id: "italic", label: "Itálico", icon: Italic, apply: (s) => wrap(null, "*", "*", s) },
  { id: "underline", label: "Sublinhado", icon: Underline, apply: (s) => wrap(null, "++", "++", s) },
  { id: "h1", label: "Título", icon: null, text: "H1", block: true, apply: (s) => `# ${s || "Título"}` },
  { id: "h2", label: "Subtítulo", icon: null, text: "H2", block: true, apply: (s) => `## ${s || "Subtítulo"}` },
  { id: "h3", label: "Subtítulo menor", icon: null, text: "H3", block: true, apply: (s) => `### ${s || "Subtítulo"}` },
  { id: "ul", label: "Lista com marcadores", icon: List, block: true, apply: (s) => (s || "item").split("\n").map((l) => `- ${l}`).join("\n") },
  { id: "ol", label: "Lista numerada", icon: ListOrdered, block: true, apply: (s) => (s || "item").split("\n").map((l, i) => `${i + 1}. ${l}`).join("\n") },
  { id: "quote", label: "Citação", icon: Quote, block: true, apply: (s) => `> ${s || "citação"}` },
  { id: "link", label: "Link", icon: Link2, apply: (s) => `[${s || "texto do link"}](https://)` },
];

/** Editor simples de Markdown: formatação sobre a seleção, sintaxe livre e prévia igual ao aplicativo. */
export const MarkdownEditor = ({ value, onChange, rows = 10, testId = "markdown-editor", label }) => {
  const ref = useRef(null);
  const [preview, setPreview] = useState(false);

  const applyAction = (action) => {
    const el = ref.current;
    const text = value || "";
    let start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const selected = text.slice(start, end);
    const replacement = action.apply(selected);
    // Títulos, listas e citações só valem no início da linha
    const prefix = action.block && start > 0 && text[start - 1] !== "\n" ? "\n" : "";
    const next = `${text.slice(0, start)}${prefix}${replacement}${text.slice(end)}`;
    start += prefix.length;
    onChange(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + replacement.length, start + replacement.length);
    });
  };

  return (
    <div data-testid={testId} className="rounded-xl border border-border bg-white">
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-2 py-2">
        {ACTIONS.map((action) => (
          <button
            key={action.id}
            type="button"
            title={action.label}
            aria-label={action.label}
            data-testid={`${testId}-${action.id}`}
            onClick={() => applyAction(action)}
            className="grid h-9 min-w-[36px] place-items-center rounded-lg px-2 text-xs font-semibold text-ink-600 transition-colors hover:bg-sand"
          >
            {action.icon ? <action.icon size={15} /> : action.text}
          </button>
        ))}
        <button
          type="button"
          data-testid={`${testId}-preview-toggle`}
          onClick={() => setPreview((v) => !v)}
          className="ml-auto inline-flex h-9 items-center gap-1.5 rounded-full bg-sand px-3 text-xs font-semibold text-ink-700 transition-colors hover:bg-sage-600 hover:text-bone"
        >
          {preview ? <Pencil size={14} /> : <Eye size={14} />}
          {preview ? "Editar" : "Visualizar conteúdo"}
        </button>
      </div>

      {preview ? (
        <div data-testid={`${testId}-preview`} className="min-h-[160px] px-4 py-3">
          {value ? <Markdown testId={`${testId}-preview-body`}>{value}</Markdown> : (
            <p className="text-sm text-ink-400">Nada para visualizar ainda.</p>
          )}
        </div>
      ) : (
        <textarea
          ref={ref}
          data-testid={`${testId}-input`}
          aria-label={label || "Texto com formatação"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          className="w-full resize-y rounded-b-xl px-4 py-3 font-mono text-[13.5px] leading-relaxed text-ink-800 outline-none"
        />
      )}
      <p className="border-t border-border px-4 py-2 text-[11.5px] text-ink-400">
        Formatação: **negrito**, *itálico*, ++sublinhado++, # Título, ## Subtítulo, - lista, 1. lista numerada,
        &gt; citação, [link](https://).
      </p>
    </div>
  );
};

export default MarkdownEditor;
